import {
  useCreateDevelopmentNote,
  useDevelopmentNotes,
} from "@coaching-culture/api-client";
import {
  Box,
  Button,
  Circle,
  EmptyState,
  Flex,
  IconToast,
  Loader,
  Panel,
  PanelHeader,
  RichTextEditor,
  Text,
  useToast,
} from "@coaching-culture/ui";
import { Comment } from "components/Comment";
import { useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperPlane,
  FaSeedling,
} from "react-icons/fa";

type Props = {
  relationshipId: string | null;
};

export function DevelopmentNotesTab({ relationshipId }: Props) {
  const { data: notes, isFetched } = useDevelopmentNotes(relationshipId);
  const createNote = useCreateDevelopmentNote();
  const pushToast = useToast();
  const [body, setBody] = useState("");

  if (relationshipId == null) {
    return (
      <Panel>
        <Flex justifyContent="center" p={4}>
          <Text color="grey2" fontWeight={600}>
            Development notes aren't available for this conversation.
          </Text>
        </Flex>
      </Panel>
    );
  }

  const submit = async () => {
    const trimmed = body.trim();
    if (trimmed === "" || trimmed === "<p></p>") {
      pushToast({
        content: (
          <IconToast
            icon={FaExclamationCircle}
            text="Please write something before saving."
            iconColor="danger"
          />
        ),
      });
      return;
    }

    try {
      await createNote.mutateAsync({ relationshipId, body: trimmed });
      setBody("");
      pushToast({
        content: (
          <IconToast
            icon={FaCheckCircle}
            text="Development note saved"
            iconColor="primary"
          />
        ),
      });
    } catch {
      pushToast({
        content: (
          <IconToast
            icon={FaExclamationCircle}
            text="Could not save the note. Please try again."
            iconColor="danger"
          />
        ),
      });
    }
  };

  return (
    <Panel>
      <PanelHeader>
        <Circle color="primary" icon={FaSeedling} mr={2} size="small" />
        <Text fontWeight={500} fontSize={4}>
          Development Notes
        </Text>
      </PanelHeader>
      <Box p={3}>
        <Text mb={2} color="textMuted">
          Notes are shared between the manager and direct report, and are
          visible across every conversation in this relationship.
        </Text>
        <RichTextEditor value={body} onChange={setBody} height={250} />
        <Flex mb={3} mt={2} justifyContent="flex-end">
          <Button
            onClick={submit}
            color="primary"
            icon={FaPaperPlane}
            disabled={createNote.isLoading}
          >
            Save Note
          </Button>
        </Flex>
        {!isFetched ? (
          <Flex justifyContent="center" p={3}>
            <Loader />
          </Flex>
        ) : notes && notes.length > 0 ? (
          notes.map((note) => <Comment key={note.id} comment={note} />)
        ) : (
          <EmptyState icon={FaSeedling} compact>
            No development notes yet.
          </EmptyState>
        )}
      </Box>
    </Panel>
  );
}
